/** Searchable Member Directory with dynamic membership-tier filter
 *  tabs. Tabs render from /orgs/{id}/membership-tiers and drive the
 *  federated /api/federation/members endpoint (scoped to this org). */
import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function MemberDirectoryPanel({ org, tiersVersion = 0 }) {
  const [members, setMembers] = useState([]);
  const [total, setTotal]     = useState(0);
  const [search, setSearch]   = useState("");
  const [tiers, setTiers]     = useState([]);
  const [activeTier, setActiveTier] = useState("");

  // Reload tier tabs when org changes or a sibling panel bumps the
  // tiersVersion counter (e.g. after MembershipTiersPanel CRUD).
  useEffect(() => {
    if (!org?.id) return;
    api.get(`/orgs/${org.id}/membership-tiers`)
       .then(({ data }) => setTiers((data.items || []).filter((t) => t.active)))
       .catch(() => setTiers([]));
  }, [org?.id, tiersVersion]);

  useEffect(() => {
    if (!org?.id) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/federation/members", {
          params: {
            org_id: org.id,
            q: search || undefined,
            tier: activeTier || undefined,
            limit: 50,
          },
        });
        setMembers(data.items || []);
        setTotal(data.total || 0);
      } catch (err) {
        console.warn("[MemberDirectoryPanel] load skipped:", err?.message);
      }
    }, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [org?.id, search, activeTier]);

  if (!org) return null;

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="members-card">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Member Directory</div>
          <h2 className="font-display text-xl uppercase tracking-tight">
            {total.toLocaleString()} registered
          </h2>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search name / phone / membership #"
                 className="h-10 w-72 bg-black/40 border-white/15 pl-9"
                 data-testid="member-search" />
        </div>
      </div>

      {/* Dynamic tier filter tabs */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="tier-tabs">
        <Button type="button"
                variant={activeTier === "" ? "default" : "ghost"}
                className={
                  "h-8 px-3 text-[11px] uppercase tracking-[0.18em] " +
                  (activeTier === ""
                    ? "bg-white/10 text-white"
                    : "text-white/55 hover:text-white")
                }
                onClick={() => setActiveTier("")}
                data-testid="tier-tab-all">
          All
        </Button>
        {tiers.map((t) => (
          <Button key={t.id} type="button"
                  variant={activeTier === t.key ? "default" : "ghost"}
                  className={
                    "h-8 px-3 text-[11px] uppercase tracking-[0.18em] " +
                    (activeTier === t.key
                      ? "bg-white/10 text-white"
                      : "text-white/55 hover:text-white")
                  }
                  onClick={() => setActiveTier(t.key)}
                  data-testid={`tier-tab-${t.key}`}>
            {t.label}
          </Button>
        ))}
      </div>

      {members.length === 0 ? (
        <div className="text-sm text-white/55 py-8 text-center">
          {search || activeTier
            ? "No matches. Try a different search term or tier."
            : <>No members yet for <span className="font-mono">{org.brand_name?.toLowerCase()}</span>. Members appear here after you issue iCards from the roster.</>}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-white/10" data-testid="members-table">
          <table className="w-full text-left">
            <thead className="bg-white/5">
              <tr className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                <th className="px-3 py-2.5">Member</th>
                <th className="px-3 py-2.5">Role</th>
                <th className="px-3 py-2.5">Tier</th>
                <th className="px-3 py-2.5">Membership #</th>
                <th className="px-3 py-2.5">City</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-white/5 hover:bg-white/[0.04]"
                    data-testid={`member-row-${m.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {m.on_the_job_today && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0"
                              title="On the job today" />
                      )}
                      {m.avatar_url
                        ? <img src={m.avatar_url} alt="" className="w-7 h-7 rounded-full border border-white/15" />
                        : <div className="w-7 h-7 rounded-full bg-white/10" />}
                      <span className="text-sm text-white">{m.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] text-white/70">{m.primary_role}</td>
                  <td className="px-3 py-2.5 text-[11px] text-white/55">
                    {m.membership_tier || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] font-mono text-white/55">{m.membership_number || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-white/55">{m.primary_association_city || m.city || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > members.length && (
            <div className="px-3 py-2 text-[11px] text-white/45 bg-white/[0.03] border-t border-white/5">
              Showing {members.length} of {total} · refine search to narrow
            </div>
          )}
        </div>
      )}
    </div>
  );
}
